import React, { useMemo } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Divider, Button, Stack } from '@mui/material';

const navy = '#1e3a5f';

/**
 * כל הזמנים ההלכתיים האפשריים שניתן להציג על ציר הזמן.
 * key חייב להתאים למה שה-API (Hebcal) מחזיר ב-fetchZmanim.
 */
export const ALL_TIMELINE_ZMANIM = [
  // ─── בוקר ───────────────────────────────────────────────
  { key: 'alotHaShachar',      label: 'עלות השחר',                          group: 'בוקר' },
  { key: 'misheyakir',         label: 'משיכיר — זמן טלית/תפילין',           group: 'בוקר' },
  { key: 'misheyakirMachmir',  label: 'משיכיר מחמיר',                        group: 'בוקר' },
  { key: 'sunrise',            label: 'הנץ החמה',                            group: 'בוקר' },

  // ─── סוף זמן שמע ────────────────────────────────────────
  { key: 'sofZmanShmaMGA',        label: 'סוף זמן קריאת שמע — מג"א (90 במע׳ / 19.8°)', group: 'סוף זמן שמע' },
  { key: 'sofZmanShmaMGA16Point1',label: 'סוף זמן קריאת שמע — מג"א (72 במע׳ / 16.1°)', group: 'סוף זמן שמע' },
  { key: 'sofZmanShma',           label: 'סוף זמן קריאת שמע — גר"א',                    group: 'סוף זמן שמע' },

  // ─── סוף זמן תפילה ──────────────────────────────────────
  { key: 'sofZmanTfillaMGA',        label: 'סוף זמן תפילה — מג"א (90 במע׳ / 19.8°)', group: 'סוף זמן תפילה' },
  { key: 'sofZmanTfillaMGA16Point1',label: 'סוף זמן תפילה — מג"א (72 במע׳ / 16.1°)', group: 'סוף זמן תפילה' },
  { key: 'sofZmanTfilla',           label: 'סוף זמן תפילה — גר"א',                    group: 'סוף זמן תפילה' },

  // ─── אמצע היום ──────────────────────────────────────────
  { key: 'chatzot',          label: 'חצות היום',          group: 'אמצע היום' },
  { key: 'minchaGedola',     label: 'מנחה גדולה — גר"א',  group: 'אמצע היום' },
  { key: 'minchaGedolaMGA',  label: 'מנחה גדולה — מג"א',  group: 'אמצע היום' },
  { key: 'minchaKetana',     label: 'מנחה קטנה — גר"א',   group: 'אמצע היום' },
  { key: 'minchaKetanaMGA',  label: 'מנחה קטנה — מג"א',   group: 'אמצע היום' },
  { key: 'plagHaMincha',     label: 'פלג המנחה',           group: 'אמצע היום' },

  // ─── ערב ────────────────────────────────────────────────
  { key: 'sunset',        label: 'שקיעת החמה',                group: 'ערב' },
  { key: 'beinHaShmashos',label: 'בין השמשות',                group: 'ערב' },
  { key: 'tzeit85deg',    label: 'צאת כוכבים 8.5° — גר"א',   group: 'ערב' },
  { key: 'tzeit7083deg',  label: 'צאת כוכבים 7° — ר"ת',      group: 'ערב' },
  { key: 'tzeit42min',    label: "צאת כוכבים 42 דק'",         group: 'ערב' },
  { key: 'tzeit50min',    label: "צאת כוכבים 50 דק'",         group: 'ערב' },
  { key: 'tzeit72min',    label: "צאת כוכבים 72 דק'",         group: 'ערב' },
  { key: 'chatzotNight',  label: 'חצות הלילה',                group: 'ערב' },

  // ─── ערב פסח (י"ד ניסן) ─────────────────────────────────
  { key: 'sofZmanAchilatChametz',    label: 'סוף זמן אכילת חמץ — גר"א',        group: 'ערב פסח' },
  { key: 'sofZmanBiurChametz',       label: 'סוף זמן ביעור חמץ — גר"א',        group: 'ערב פסח' },
  { key: 'sofZmanAchilatChametzMGA', label: 'סוף זמן אכילת חמץ — מג"א (72 במע׳)', group: 'ערב פסח' },
  { key: 'sofZmanBiurChametzMGA',    label: 'סוף זמן ביעור חמץ — מג"א (72 במע׳)', group: 'ערב פסח' },
];

/** הזמנים המוצגים כברירת מחדל כשלא הוגדרה בחירה */
export const DEFAULT_VISIBLE_ZMANIM = [
  'alotHaShachar', 'sunrise',
  'sofZmanShmaMGA', 'sofZmanShma',
  'sofZmanTfillaMGA', 'sofZmanTfilla',
  'chatzot', 'minchaGedola',
  'sunset', 'tzeit85deg',
  'chatzotNight',
];

const GROUP_ORDER = ['בוקר', 'סוף זמן שמע', 'סוף זמן תפילה', 'אמצע היום', 'ערב', 'ערב פסח'];

const GROUP_NOTE = {
  'ערב פסח': 'הזמנים מחושבים כל יום — רלוונטיים הלכתית ביום י"ד ניסן בלבד',
};

/**
 * ZmanimVisibilityStep
 * שלב ויזרד לבחירת אילו זמנים הלכתיים יוצגו על ציר הזמן.
 *
 * Props:
 *   formData  { visibleZmanim: string[] | null }
 *   onChange  (partial) => void
 */
const ZmanimVisibilityStep = ({ formData, onChange }) => {
  const selected = useMemo(() => {
    if (Array.isArray(formData?.visibleZmanim)) return new Set(formData.visibleZmanim);
    return new Set(DEFAULT_VISIBLE_ZMANIM);
  }, [formData?.visibleZmanim]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange({ visibleZmanim: [...next] });
  };

  const selectAll = () => onChange({ visibleZmanim: ALL_TIMELINE_ZMANIM.map(z => z.key) });
  const selectDefaults = () => onChange({ visibleZmanim: [...DEFAULT_VISIBLE_ZMANIM] });
  const deselectAll = () => onChange({ visibleZmanim: [] });

  const grouped = useMemo(() => {
    const map = {};
    ALL_TIMELINE_ZMANIM.forEach(z => {
      if (!map[z.group]) map[z.group] = [];
      map[z.group].push(z);
    });
    return map;
  }, []);

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        זמנים הלכתיים על הציר
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
        בחר אילו זמנים הלכתיים יופיעו על ציר הזמן — רק הזמנים המסומנים יוצגו.
        זמנים לא מסומנים לא יוסרו מהחישוב, רק מהתצוגה.
      </Typography>

      {/* כפתורי בחירה מהירה */}
      <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 0.5 }}>
        <Button
          size="small"
          onClick={selectDefaults}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.5,
            border: '1px solid #d1d5db', borderRadius: '8px', color: navy,
            '&:hover': { bgcolor: '#f0f4fa', borderColor: navy },
          }}
        >
          ברירת מחדל
        </Button>
        <Button
          size="small"
          onClick={selectAll}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.5,
            border: '1px solid #d1d5db', borderRadius: '8px', color: '#6b7280',
            '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' },
          }}
        >
          בחר הכל
        </Button>
        <Button
          size="small"
          onClick={deselectAll}
          sx={{
            fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.5,
            border: '1px solid #d1d5db', borderRadius: '8px', color: '#9ca3af',
            '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' },
          }}
        >
          נקה הכל
        </Button>
        <Typography sx={{ fontSize: '0.73rem', color: '#9ca3af', alignSelf: 'center', mr: 0.5 }}>
          {selected.size} מסומנים
        </Typography>
      </Stack>

      {/* קבוצות זמנים */}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
        {GROUP_ORDER.map((group, gi) => (
          <Box key={group}>
            {gi > 0 && <Divider />}
            {/* כותרת קבוצה */}
            <Box sx={{ px: 2, py: 0.75, bgcolor: '#f9fafb' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {group}
              </Typography>
              {GROUP_NOTE[group] && (
                <Typography sx={{ fontSize: '0.68rem', color: '#d97706', mt: 0.25 }}>
                  ⚠ {GROUP_NOTE[group]}
                </Typography>
              )}
            </Box>
            {/* זמנים בקבוצה */}
            <Box sx={{ px: 1 }}>
              {(grouped[group] || []).map((zman, i) => {
                const isChecked = selected.has(zman.key);
                return (
                  <FormControlLabel
                    key={zman.key}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggle(zman.key)}
                        size="small"
                        sx={{
                          color: '#d1d5db',
                          '&.Mui-checked': { color: navy },
                          py: 0.5,
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.82rem', color: isChecked ? '#111827' : '#9ca3af', fontWeight: isChecked ? 500 : 400 }}>
                        {zman.label}
                      </Typography>
                    }
                    sx={{
                      display: 'flex', width: '100%', mx: 0,
                      py: 0.25,
                      borderBottom: i < (grouped[group]?.length - 1) ? '1px solid #f3f4f6' : 'none',
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      {selected.size === 0 && (
        <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', mt: 1.5, textAlign: 'center' }}>
          לא נבחר אף זמן — הציר יהיה ריק מזמנים הלכתיים
        </Typography>
      )}
    </Box>
  );
};

export default ZmanimVisibilityStep;
